<template>
    <search-form-layout :popup="popup" title="activities.activities" @close-popup="$emit('close-popup')">
        <template v-slot:form>
            <v-form ref="form" v-model="valid">
                <v-row>
                    <v-col v-if="!searchWithoutDestination" md="4" cols="12">
                        <city-autocomplete
                            ref="locationAutocomplete"
                            v-model="city"
                            filled
                            :rules="[v => !!v || `${$t('destination')} ${$t('validation.required')}`]"
                            :default-cities="popularDestinations"
                            product-type="ACTIVITY"
                        />
                    </v-col>
                    <v-col :md="searchWithoutDestination ? 8 : 4" cols="12">
                        <daterange-m
                            :_start="startDate"
                            :_end="endDate"
                            @change-start="startDate = $event"
                            @change-end="endDate = $event"
                        />
                    </v-col>
                    <v-col :md="searchWithoutDestination ? 4 : 2" cols="12">
                        <tourists-select-menu
                            :_adults="adults"
                            :_children-ages="childrenAges"
                            token="guest"
                            :max-total="50"
                            @change-adults="adults = $event"
                            @change-children-ages="childrenAges = $event"
                        />
                    </v-col>
                    <v-col md="2" cols="12" class="hidden-sm-and-down">
                        <v-btn block size="x-large" color="primary" :disabled="!valid" @click="search">
                            {{ $t('search') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-form>
            <additional-options-wrapper
                ref="addForm"
                :suppliers="suppliers"
                :options="['freeOnly', 'internalSuppliers', 'externalSuppliers', 'supplierId']"
            />
            <v-row class="hidden-md-and-up">
                <v-col md="2" cols="12">
                    <v-btn block size="x-large" color="primary" :disabled="!valid" @click="search">
                        {{ $t('search') }}
                    </v-btn>
                </v-col>
            </v-row>
        </template>
    </search-form-layout>
</template>

<script lang="ts">
    import {Component, mixins, Prop} from 'vue-facing-decorator'
    import CityAutocomplete from '~/components/search/forms/CityAutocomplete.vue'
    import SearchFormLayout from '~/components/parts/SearchFormLayout.vue'
    import TouristsSelectMenu from '~src/components/search/forms/touristsSelectMenu.src'
    import {AdditionalSearchOptionsMixin} from '@/mixins/AdditionalSearchOptionsMixin.vue'
    import AdditionalOptionsWrapper from '~src/components/snippets/AdditionalSearchOptions/additionalOptionsWrapper.src'
    import {CHANGE_LOCALE_EVENT, CHANGE_TAB_MOBILE_EVENT, EventBus} from '@/utils/event-bus'
    import DaterangeM from '~/components/search/forms/DaterangeM.vue'
    import {simpleTourStore} from '~/composables/store-accessor'

    @Component({
        components: {
            DaterangeM,
            TouristsSelectMenu,
            CityAutocomplete,
            SearchFormLayout,
            AdditionalOptionsWrapper,
        },
    })
    export class SimpleTourSearchForm extends mixins(AdditionalSearchOptionsMixin) {
        @Prop({default: false}) popup
        @Prop() cityId

        city = null
        startDate = null
        endDate = null
        adults = 2
        childrenAges = []
        valid = false
        popularDestinations = []
        suppliers = []

        beforeUnmount() {
            EventBus.$off(CHANGE_LOCALE_EVENT, this.reload)
        }

        async reload() {
            await this.loadPopularDestinations()
            this.$refs.locationAutocomplete.clear()
            if (this.city && this.city.id) {
                try {
                    const {
                        cities: [city],
                    } = await this.$api.locations.get({id: this.city.id, limitCities: 1})
                    this.city = city
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            } else {
                this.$refs.locationAutocomplete.load()
            }
        }

        newQuery() {
            const query = {
                ...(this.city?.id && {cityId: this.city.id}),
                startDate: this.startDate,
                endDate: this.endDate,
                adults: this.adults,
                childrenAges: this.childrenAges,
            }

            if (persistentStore.selectedCompanyAndAgent) {
                query.clientId = persistentStore.selectedCompanyAndAgent.company.id

                if (persistentStore.selectedCompanyAndAgent.agent) {
                    query.agentId = persistentStore.selectedCompanyAndAgent.agent.personId
                }
            }

            return query
        }

        async search() {
            this.$emit('close-popup')
            const query = this.newQuery()
            if (this.city) simpleTourRuntimeStore.SET_CITY(this.city)
            const name = 'simpleTourSearch'
            if (this.$route.name !== name) {
                simpleTourRuntimeStore.newSearch()
            }
            try {
                await this.$router.push({name, query})
            } catch (e) {
                console.error(e)
                await simpleTourRuntimeStore.search(this.searchRequest)
            }
            EventBus.$emit(CHANGE_TAB_MOBILE_EVENT, 1)
        }

        async mounted() {
            EventBus.$on(CHANGE_LOCALE_EVENT, this.reload)
            await this.$store.restored
            await this.loadPopularDestinations()
            await this.loadCity()
            if (this.$dateFns.isAfter(this.$dateFns.parseISO(this.searchRequest.startDate), new Date())) {
                this.startDate = this.searchRequest.startDate
                this.endDate = this.searchRequest.endDate
            } else {
                this.startDate = this.$dateFns.format(this.$dateFns.addDays(new Date(), 1))
                this.endDate = this.$dateFns.format(this.$dateFns.addDays(new Date(), 7))
            }
            //TODO Need refactoring with other products
            this.adults = authStore.user.isEmployeeOrExternalCorp ? 1 : this.searchRequest.adults
            this.childrenAges = authStore.user.isEmployeeOrExternalCorp ? [] : [...this.searchRequest.childrenAges]
            if (this.isTO1) {
                try {
                    this.suppliers = (await this.$api.suppliers.get({productType: 'tour'}))?.suppliers
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }
        }

        async loadCity() {
            const cityId = this.cityId || this.searchRequest.cityId
            if (cityId && simpleTourRuntimeStore.city.id !== cityId) {
                await simpleTourRuntimeStore.loadCity(this.cityId)
            }
            this.city = cityId && Object.keys(simpleTourRuntimeStore.city).length ? simpleTourRuntimeStore.city : null
        }

        async loadPopularDestinations() {
            try {
                // TODO[p4] add productType
                this.popularDestinations = (await this.$api.locations.get({limitCities: 7})).cities
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        get isTO1() {
            return authStore.isTO1
        }

        get searchRequest() {
            return simpleTourStore.searchRequest
        }

        get searchWithoutDestination() {
            return false
        }
    }

    export default SimpleTourSearchForm
</script>

<style lang="scss" scoped>
    :deep(.v-label) {
        font-size: inherit;
    }
</style>
